import { Suspense, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Navigate, Route, Routes as RoutesReactRouterDom } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { logOut } from '../app/loginReducer';
import { LogoutRequestBody } from '../app/models/auth/loginUser';
import { logout } from '../http/authentication-services';

import env from '../environment';

import { GenericNotFound, Login } from './paths';
import Menu from '../components/Menu/Menu';
import AppInitialize from '../components/AppInitialize/AppInitialize';
import TemplateList from '../components/Templates/TemplateList';
import CreateTemplateForm from '../components/Templates/CreateTemplate/CreateTemplateForm';
import CreateOrganization from '../components/Organizations/CreateOrganization';
import AdminList from '../components/Admins/AdminList';
import AdminContainer from '../components/Admins/AdminContainer';
import AdminDetails from '../components/Admins/AdminDetails';
import OrganizationsList from '../components/Organizations/OrganizationsList';
import ShowOrganization from '../components/Organizations/ShowOrganization';
import Register from '../components/Register/Register.container';
import RoleDetail from '../components/Roles/RoleDetail';
import RoleList from '../components/Roles/RoleList';
import PermissionSetList from '../components/PermissionSet/PermissionSetList';
import ShowPermissionSet from '../components/PermissionSet/ShowPermissionSet';
import ApplicationSettings from '../components/AppSettings/ApplicationSettings';
import TemplateViewer from '../components/Templates/TemplateViewer';
import SurveyList from '../components/Survey/Template/SurveyList';
import SurveyTemplateDetail from '../components/Survey/Template/TemplateDetail';
import QuestionList from '../components/Survey/Question/QuestionList';
import NewQuestion from '../components/Survey/Question/NewQuestion';
import { RouteConstants } from '../constants/RouteConstants';
import { useTranslation } from 'react-i18next';
import ProtectRoute from '../components/RoutePermission/ProtectRoute';
import { AppConstants } from '../constants/AppConstants';
import { APIKeyConstants } from '../constants/APIKeyConstants';
import ConfigurationListComponent from '../components/ApplicationConfiguration/ConfigurationListComponent';
import ConfigurationComponent from '../components/ApplicationConfiguration/Configuration';
import ConfigurationLanguageComponent from '../components/ApplicationConfiguration/ConfigurationLanguage';
import ConfigurationImageComponent from '../components/ApplicationConfiguration/ConfigurationImage';
import ConsentContainer from '../components/Consent/consentContainer';
import FieldGroupDetails from '../components/Consent/fieldGroupDetails';
import ConsentTemplateDetails from '../components/Consent/consentTemplateDetails';
import MetadataList from '../components/Metadata/MetadataList';
import CreateMetadata from '../components/Metadata/CreateMetadata';
import ConfigurationService from '../components/ConfigurationService/ConfigurationService';
import ConfigKeyList from '../components/ConfigurationService/ConfigKeyList';
import ConfigKeyDetails from '../components/ConfigurationService/ConfigKeyDetails';
import CategoriesList from '../components/ConfigurationService/CategoriesList';
import CategoryDetails from '../components/ConfigurationService/CategoryDetails';
import CreateObject from '../components/Metadata/CreateObject';
import ObjectList from '../components/Metadata/ObjectList';
import { CustomComponent } from '../components/Custom/Custom';
import Modal from '../components/shared/ModalPortal/Modal';
import BiButton from '../components/primitives/buttons/BiButton.primitive';
import InstanceList from '../components/Metadata/InstanceList';
import CreateInstance from '../components/Metadata/CreateInstance';
import MigrationContainer from '../components/Migration/MigrationContainer';
import ExportDetail from '../components/Migration/Details/ExportDetail';
import ImportDetail from '../components/Migration/ImportDetails/ImportDetail';
import TemplateDetail from '../components/Templates/TemplateDetail';
import { fetchFromCookie } from '../util/admin-utils';
import PasswordPolicyList from '../components/PasswordPolicy/PasswordPolicyList';
import PasswordPolicy from '../components/PasswordPolicy/PasswordPolicy';
import BucketList from '../components/FileManagement/BucketList';
import BucketDetail from '../components/FileManagement/BucketDetail';
import NewFolder from '../components/FileManagement/NewFolder';
import RoleManagement from '../components/Roles/RoleManagement';
import FolderDetails from '../components/FileManagement/FolderDetails';
import FileVersionHistoryList from '../components/FileManagement/FileHistoryList';
import CarePlanTemplateList from '../components/CarePlan/CarePlanTemplateList';
import CarePlanTemplate from '../components/CarePlan/CarePlanTemplate';
import AddNewLanguage from '../components/ApplicationConfiguration/AddNewLanguage';
import AccessManagementSettings from '../components/SecuritySettings/AccessManagementSettings';
import NoteDetails from '../components/CarePlan/Notes/NoteDetails';
import CarePlanNotesList from '../components/CarePlan/Notes/NoteList';
import CarePlanActivities from '../components/CarePlan/Activity/ActivityList';
import ActivityDetails from '../components/CarePlan/Activity/ActivityDetails';
import FeaturesList from '../components/Features/FeaturesList';
import FeatureDetails from '../components/Features/FeatureDetails';

export const Routes = () => {
  let warningTime: any = useRef(null);
  let logoutTime: any = useRef(null);
  let remainingTime: any = useRef(null);

  const dispatch = useAppDispatch();
  const [showAccountLockout, setShowAccountLockout] = useState(false);
  const [showInactivityDialog, setShowInactivityDialog] = useState(false);
  const [timerText, setTimerText] = useState('');
  const loggedIn = useAppSelector((state) => state.login.isLoggedIn);

  const initialDataLoaded = useAppSelector((state) => state.initialLoadData.initialDataLoaded);

  const solutionConfigurationDefaults: any = useAppSelector(
    (state) => state.configuration.solutionConfigurationDefaults
  );
  const { t } = useTranslation();

  document.onmousemove = resetTimer;
  document.onkeydown = resetTimer;

  if (solutionConfigurationDefaults?.dataGrid) {
    if (solutionConfigurationDefaults?.dataGrid?.defaultPageSize) {
      AppConstants.DEFAULT_PAGE_SIZE = solutionConfigurationDefaults?.dataGrid.defaultPageSize;
    }
    if (solutionConfigurationDefaults?.dataGrid?.pages) {
      AppConstants.DEFAULT_PAGE_SIZES = solutionConfigurationDefaults?.dataGrid.pages;
    }
  }

  if (solutionConfigurationDefaults?.enrollChildOrgOnly !== undefined) {
    AppConstants.ENROLL_ORG_CHILD_ONLY = solutionConfigurationDefaults?.enrollChildOrgOnly;
  }

  function resetTimer() {
    const { session = {} } = solutionConfigurationDefaults;
    const {
      inactivityLogout: inactivityLogoutTime = AppConstants.DEFAULT_INACTIVITY_LOGOUT_TIME,
      inactivityWarning = AppConstants.DEFAULT_INACTIVITY_WARNING_TIME
    } = session;
    // const inactivityLogouttime = 120000;
    // const inactivityWarningtime = 60000;

    // "inactivityLogout": 1800000,
    //     "inactivityWarning": 1500000

    if (loggedIn && !showInactivityDialog) {
      clearTimeout(warningTime.current);
      clearTimeout(logoutTime.current);
      warningTime.current = setTimeout(() => warnInactivity(), inactivityWarning);
      logoutTime.current = setTimeout(() => inactivityLogout(), inactivityLogoutTime);
    }
  }

  async function inactivityLogout() {
    clearInterval(remainingTime.current);
    setShowInactivityDialog(false);
    const logoutBody: LogoutRequestBody = {
      client_id: 'oidc_pkce',
      token: fetchFromCookie('access-token') || '' // localStorage.getItem('access-token') || ''
    };
    await logout(logoutBody);
    dispatch(logOut());
    return `${t('T_LOGOUT_MSG')}`;
  }

  function warnInactivity() {
    const { session = {} } = solutionConfigurationDefaults;
    let {
      inactivityLogout = AppConstants.DEFAULT_INACTIVITY_LOGOUT_TIME,
      inactivityWarning = AppConstants.DEFAULT_INACTIVITY_WARNING_TIME
    } = session;

    // let inactivityLogout = 120000;
    // let inactivityWarning = 60000;
    let remainingTimeText = '';
    clearInterval(remainingTime.current);
    remainingTime.current = setInterval(() => {
      const timeleft = inactivityLogout - inactivityWarning;
      let minutes, seconds;
      minutes = Math.floor((timeleft % (1000 * 60 * 60)) / (1000 * 60));
      seconds = Math.floor((timeleft % (1000 * 60)) / 1000);
      if (timeleft < 0) {
        clearInterval(remainingTime.current);
      }
      inactivityWarning = inactivityWarning + 1000;
      remainingTimeText = `${minutes} minutes and ${seconds} seconds.`;
      setTimerText(remainingTimeText);
      setShowInactivityDialog(true);
    }, 1000);

    return remainingTimeText;
  }

  return (
    <Suspense fallback={<p>Loading</p>}>
      {loggedIn && showInactivityDialog && (
        <Modal
          overflowVisible={true}
          showCloseIcon={true}
          width="30rem"
          showHeader={true}
          headerText={t('T_SESSION_TIMEOUT_HEADER')}
          toggleVisiblity={(value: boolean) => {
            clearInterval(remainingTime.current);
            setShowInactivityDialog(false);
          }}
          isVisible={showInactivityDialog}
          body={
            <>
              <div className="mb-5 flex flex-col">
                <div className="">{`${t('T_SESSION_TIMEOUT_BODY_TEXT')} ${timerText}`}</div>
              </div>

              <ModalButton>
                <BiButton
                  className={'mr-2 text-primary'}
                  type="button"
                  onClick={() => {
                    clearInterval(remainingTime.current);
                    setShowInactivityDialog(false);
                  }}
                >
                  {t('T_SESSION_STAY_SIGNED_IN')}
                </BiButton>
                <BiButton
                  className={'bg-primary text-white'}
                  type="button"
                  onClick={() => {
                    inactivityLogout();
                  }}
                  // disabled={!permissions.create}
                >
                  {t('T_SESSION_SIGN_OUT')}
                </BiButton>
              </ModalButton>
            </>
          }
        />
      )}
      {loggedIn && (
        <Menu modalIsActive={false}>
          <AppInitialize>
            {initialDataLoaded && (
              <RoutesReactRouterDom>
                {/* <Route path="/profile" element={<ProfileMainView />} /> */}
                <Route path={RouteConstants.ROUTE_PROFILE} element={<AdminDetails />} />
                <Route path="/404" element={<GenericNotFound />} />
                <Route
                  path="/templates/list"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.NOTIFICATION_TEMPLATE_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <TemplateList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path="/templates/list/:id"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.NOTIFICATION_TEMPLATE_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <TemplateList />
                    </ProtectRoute>
                  }
                />

                {/* <Route
                  path="/templates/edit/:id"
                  element={
                    <ProtectRoute
                      apiPermissions={[AppConstants.PERMISSION_NOTIFICATION_TEMPLATE_UPDATE]}
                    >
                      <TemplateViewer />
                    </ProtectRoute>
                  }
                /> */}
                <Route
                  path={RouteConstants.NOTIFICATION_TEMPLATE_DETAIL_EDIT}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.NOTIFICATION_TEMPLATE_MANAGEMENT_UPDATE
                      }
                      allOperations={false}
                    >
                      <TemplateDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.NOTIFICATION_TEMPLATE_DETAIL}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.NOTIFICATION_TEMPLATE_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <TemplateDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.NOTIFICATION_TEMPLATE_DETAIL_CREATE}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.NOTIFICATION_TEMPLATE_MANAGEMENT_CREATE
                      }
                      allOperations={false}
                    >
                      <TemplateDetail />
                    </ProtectRoute>
                  }
                />

                <Route
                  path="/role-management/create"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ROLE_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <RoleDetail />
                    </ProtectRoute>
                  }
                />

                <Route
                  path="/role-management/edit/:id"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ROLE_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <RoleDetail />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_ROLE_MANAGEMENT_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ROLE_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <RoleList />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_ROLE_MANAGEMENT_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ROLE_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <RoleManagement />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_ROLE_MANAGEMENT_EDIT}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ROLE_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <RoleManagement />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_ADMIN_LIST_ACTIVE_USERS}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.USER_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <AdminContainer defaultTab="Active Users" />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_ADMIN_LIST_INVITED_USERS}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.USER_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <AdminContainer defaultTab="Invited Users" />
                    </ProtectRoute>
                  }
                />

                {/* <Route path="/admin-list" element={<AdminList />} /> */}
                {/* <Route path="/admin-list/:id" element={<AdminList />} /> */}
                <Route
                  path="/admin-list/:id"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.USER_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <AdminContainer defaultTab="Active Users" />
                    </ProtectRoute>
                  }
                />
                <Route
                  path="/admin-list/invited/:id"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.USER_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <AdminContainer defaultTab="Invited Users" />
                    </ProtectRoute>
                  }
                />
                <Route path={RouteConstants.ROUTE_USER_DETAILS} element={<AdminDetails />} />
                <Route
                  path={RouteConstants.ROUTE_INVITED_USER_DETAILS}
                  element={<AdminDetails />}
                />
                <Route
                  path="/organization/new"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ORGANIZATION_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <CreateOrganization />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_OS_ORGANIZATION_EDIT}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ORGANIZATION_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <CreateOrganization />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_OS_ORGANIZATION_LISTING}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ORGANIZATION_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <OrganizationsList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_OS_ORGANIZATION_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.ORGANIZATION_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <ShowOrganization />
                    </ProtectRoute>
                  }
                />
                <Route path="/permissionset-list" element={<PermissionSetList />} />
                <Route path="/permission/:id" element={<ShowPermissionSet />} />
                <Route path="/app-settings" element={<ApplicationSettings DMSsetting={true} />} />
                <Route
                  path={RouteConstants.ROUTE_SURVEY_TEMPLATE_LISTING}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.SURVEY_TEMPLATE_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <SurveyList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SURVEY_TEMPLATE_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.SURVEY_TEMPLATE_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <SurveyTemplateDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SURVEY_TEMPLATE_EDIT}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.SURVEY_TEMPLATE_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <SurveyTemplateDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SURVEY_TEMPLATE_DETAILS}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.SURVEY_TEMPLATE_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <SurveyTemplateDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path="/survey-question/list"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_INSTANCE_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <QuestionList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path="/survey-question/new"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_INSTANCE_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <NewQuestion />
                    </ProtectRoute>
                  }
                />
                <Route
                  path="/survey-question/edit/:id"
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_INSTANCE_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <NewQuestion />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MOBILE_CONFIG_LIST}
                  element={<ConfigurationListComponent />}
                />
                <Route
                  path={RouteConstants.ROUTE_MOBILE_CONFIG_STYLE}
                  element={<ConfigurationComponent />}
                />
                <Route
                  path={RouteConstants.ROUTE_MOBILE_CONFIG_LANGUAGE_TRANSLATION}
                  element={<ConfigurationLanguageComponent />}
                />
                <Route
                  path={RouteConstants.ROUTE_MOBILE_CONFIG_LANGUAGE_TRANSLATION_NEW}
                  element={<AddNewLanguage />}
                />
                <Route
                  path={RouteConstants.ROUTE_MOBILE_CONFIG_CONFIGURATION}
                  element={<ConfigurationComponent />}
                />
                <Route
                  path={RouteConstants.ROUTE_MOBILE_CONFIG_FILE}
                  element={<ConfigurationImageComponent />}
                />

                <Route
                  path={RouteConstants.ROUTE_WEB_CONFIG_LIST}
                  element={<ConfigurationListComponent />}
                />
                <Route
                  path={RouteConstants.ROUTE_WEB_CONFIG_STYLE}
                  element={<ConfigurationComponent />}
                />
                <Route
                  path={RouteConstants.ROUTE_WEB_CONFIG_LANGUAGE_TRANSLATION}
                  element={<ConfigurationLanguageComponent />}
                />
                <Route
                  path={RouteConstants.ROUTE_WEB_CONFIG_LANGUAGE_TRANSLATION_NEW}
                  element={<AddNewLanguage />}
                />
                <Route
                  path={RouteConstants.ROUTE_WEB_CONFIG_CONFIGURATION}
                  element={<ConfigurationComponent />}
                />
                <Route
                  path={RouteConstants.ROUTE_WEB_CONFIG_FILE}
                  element={<ConfigurationImageComponent />}
                />

                <Route
                  path={RouteConstants.ROUTE_MDS_APPLN_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_APPLICATION_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <MetadataList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MDS_APPLN_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_APPLICATION_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <CreateMetadata />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MDS_APPLN_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_APPLICATION_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <CreateMetadata />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_MDS_APPLN_EDIT}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_APPLICATION_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <CreateMetadata />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_MDS_OBJECT_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_OBJECT_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <ObjectList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MDS_OBJECT_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_OBJECT_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <CreateObject />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MDS_OBJECT_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_OBJECT_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <CreateObject />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MDS_OBJECT_EDIT}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_OBJECT_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <CreateObject />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MDS_INSTANCE_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_INSTANCE_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <InstanceList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MDS_INSTANCE_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_INSTANCE_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <CreateInstance />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MDS_INSTANCE_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.METADATA_INSTANCE_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <CreateInstance />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_CONSENTS_FIELDGROUP}
                  element={<ConsentContainer defaultTab="Field Groups" />}
                />
                <Route
                  path={RouteConstants.ROUTE_CONSENTS}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CONSENT_TEMPLATE_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <ConsentContainer defaultTab="Consent Templates" />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CONSENTS_FIELDGROUP_DETAILS}
                  element={<FieldGroupDetails />}
                />
                <Route
                  path={RouteConstants.ROUTE_CONSENTS_DETAILS}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CONSENT_TEMPLATE_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <ConsentTemplateDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_NEW_CONSENTS}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CONSENT_TEMPLATE_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <ConsentTemplateDetails isCreateNew={true} />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MIGRATION_EXPORT_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.MIGRATION_EXPORT_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <MigrationContainer defaultTab={'EXPORT'} />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MIGRATION_IMPORT_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.MIGRATION_IMPORT_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <MigrationContainer defaultTab={'IMPORT'} />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MIGRATION_EXPORT_DETAIL_EDIT}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.MIGRATION_EXPORT_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <ExportDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MIGRATION_EXPORT_DETAIL_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.MIGRATION_EXPORT_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <ExportDetail />
                    </ProtectRoute>
                  }
                ></Route>
                <Route
                  path={RouteConstants.ROUTE_MIGRATION_IMPORT_DETAIL_EDIT}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.MIGRATION_IMPORT_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <ImportDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_MIGRATION_IMPORT_DETAIL_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.MIGRATION_IMPORT_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <ImportDetail />
                    </ProtectRoute>
                  }
                ></Route>
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={loggedIn ? RouteConstants.ROUTE_OS_ORGANIZATION_LISTING : 'auth'}
                    />
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_CONFIGURATIONSERVICE_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CONFIGURATION_SETTING_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <ConfigKeyList />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_ORG_CONFIGURATIONSERVICE_LIST}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_SETTING_MANAGEMENT_ORG_VIEW
                      }
                      allOperations={false}
                    >
                      <ConfigKeyList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_USER_CONFIGURATIONSERVICE_LIST}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_SETTING_MANAGEMENT_USER_VIEW
                      }
                      allOperations={false}
                    >
                      <ConfigKeyList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CONFIG_KEY_UPDATE}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_SETTING_MANAGEMENT_UPDATE
                      }
                      allOperations={false}
                    >
                      <ConfigKeyDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_ORG_CONFIG_KEY_UPDATE}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_SETTING_MANAGEMENT_ORG_VIEW //TODO
                      }
                      allOperations={false}
                    >
                      <ConfigKeyDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_USER_CONFIG_KEY_UPDATE}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_SETTING_MANAGEMENT_USER_VIEW //TODO
                      }
                      allOperations={false}
                    >
                      <ConfigKeyDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CONFIG_KEY_CREATE}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_SETTING_MANAGEMENT_CREATE
                      }
                      allOperations={false}
                    >
                      <ConfigKeyDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CATEGORIES_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CONFIGURATION_CATEGORY_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <CategoriesList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_ORG_CATEGORIES_LIST}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_CATEGORY_MANAGEMENT_ORG_VIEW
                      }
                      allOperations={false}
                    >
                      <CategoriesList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CATEGORIES_EDIT}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_CATEGORY_MANAGEMENT_UPDATE
                      }
                      allOperations={false}
                    >
                      <CategoryDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CATEGORIES_CREATE}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.CONFIGURATION_CATEGORY_MANAGEMENT_CREATE
                      }
                      allOperations={false}
                    >
                      <CategoryDetails />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_UMS_PASSWORD_POLICY_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.PASSWORD_POLICY_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <PasswordPolicyList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_UMS_PASSWORD_POLICY_VIEW}
                  element={
                    <ProtectRoute
                      featureName={
                        AppConstants.FEATURE_KEYS.PASSWORD_POLICY_MANAGEMENT_UPDATE //TODO
                      }
                      allOperations={false}
                    >
                      <PasswordPolicy />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_UMS_PASSWORD_POLICY_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.PASSWORD_POLICY_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <PasswordPolicy />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <CarePlanTemplateList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_NOTE_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <NoteDetails />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_NOTE_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <NoteDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_NOTE_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <CarePlanNotesList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_NOTE_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <CarePlanTemplate />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_ACTIVITY_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <CarePlanActivities />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_ACTIVITY_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <ActivityDetails />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_ACTIVITY_MANAGEMENT_UPDATE}
                      allOperations={false}
                    >
                      <ActivityDetails />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.CAREPLAN_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <CarePlanTemplate />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_AMS_FILES}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.BUCKET_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <BucketList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_AMS_FILE_DETAILS}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.BUCKET_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <BucketDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_AMS_FILES_CREATE_NEW_BUCKET}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.BUCKET_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <BucketDetail />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_AMS_FOLDER_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.BUCKET_MANAGEMENT_FILES}
                      allOperations={false}
                    >
                      <NewFolder />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_AMS_FOLDER_NESTED_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.BUCKET_MANAGEMENT_FILES}
                      allOperations={false}
                    >
                      <NewFolder />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_AMS_FILES_HISTORY}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.FILE_MANAGEMENT_VESION}
                      allOperations={false}
                    >
                      <FileVersionHistoryList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_AMS_FOLDER_NEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.FOLDER_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <FolderDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_AMS_FOLDER_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.FOLDER_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <FolderDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_AMS_FOLDER_NESTED_NEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.FOLDER_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <FolderDetails />
                    </ProtectRoute>
                  }
                />

                <Route
                  path={RouteConstants.ROUTE_SECURITY_SETTINGS}
                  element={
                    <AccessManagementSettings defaultTab={`${t('T_SESSION_TOKEN_SETTINGS')}`} />
                  }
                ></Route>

                <Route
                  path={RouteConstants.ROUTE_SECURITY_SETTINGS_OAUTH}
                  element={
                    <AccessManagementSettings defaultTab={`${t('T_OAUTH_TOKEN_SETTINGS')}`} />
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SECURITY_SETTINGS_OAUTH_PORTAL_CLIENT}
                  element={
                    <AccessManagementSettings defaultTab={`${t('T_OAUTH_TOKEN_SETTINGS')}`} />
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SECURITY_SETTINGS_OAUTH_MOBILE_CLIENT}
                  element={
                    <AccessManagementSettings defaultTab={`${t('T_OAUTH_TOKEN_SETTINGS')}`} />
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SECURITY_SETTINGS_OAUTH_OIDC_PKCE}
                  element={
                    <AccessManagementSettings defaultTab={`${t('T_OAUTH_TOKEN_SETTINGS')}`} />
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SECURITY_SETTINGS_OAUTH_SERVICE_ACCOUNT_CLIENT}
                  element={
                    <AccessManagementSettings defaultTab={`${t('T_OAUTH_TOKEN_SETTINGS')}`} />
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SECURITY_SETTINGS_AUTHENTICATION}
                  element={
                    <AccessManagementSettings defaultTab={`${t('T_AUTHENTICATION_SETTINGS')}`} />
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_SECURITY_SETTINGS_ACCOUNT_LOCKOUT}
                  element={
                    <AccessManagementSettings defaultTab={`${t('T_ACCOUNT_LOCKOUT_SETTINGS')}`} />
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_FEATURES_LIST}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.FEATURE_MANAGEMENT_VIEW}
                      allOperations={false}
                    >
                      <FeaturesList />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_FEATURES_CREATE}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.FEATURE_MANAGEMENT_CREATE}
                      allOperations={false}
                    >
                      <FeatureDetails />
                    </ProtectRoute>
                  }
                />
                <Route
                  path={RouteConstants.ROUTE_FEATURES_VIEW}
                  element={
                    <ProtectRoute
                      featureName={AppConstants.FEATURE_KEYS.FEATURE_MANAGEMENT_READ}
                      allOperations={false}
                    >
                      <FeatureDetails />
                    </ProtectRoute>
                  }
                />
              </RoutesReactRouterDom>
            )}
          </AppInitialize>
        </Menu>
      )}
      {!loggedIn && (
        <RoutesReactRouterDom>
          <Route path="/auth" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="*"
            element={
              <Navigate to={loggedIn ? RouteConstants.ROUTE_OS_ORGANIZATION_LISTING : 'auth'} />
            }
          />
        </RoutesReactRouterDom>
      )}
    </Suspense>
  );
};

export const ModalButton = styled('div')`
  display: flex;
  justify-content: end;
`;
